/**
 * Workspaces.
 *
 * Visual Reference: `design/screens/settings-workspaces.html` — states default,
 * **atlimit**, **access**, **archive**, loading, error. Its own note on the
 * loading frame is worth keeping: "Empty cannot occur: one workspace always
 * exists." Sign-up creates one and `archiveWorkspace` refuses to take the last,
 * so the empty state has no way to happen and is not built.
 *
 * The subtitle is the story's goal in the product's own words — "Workspaces
 * keep clients separate. People you invite to one can't see the others." —
 * which is PRD Appendix A's tenant boundary said to somebody non-technical.
 * RULE-008 is what makes it true; this screen is what makes it legible.
 *
 * THE COUNT IS THE PLAN'S. `3 of 3 used` on Pro, from `config` via
 * `services/entitlements` — the mockup's "3 of 5" is wrong and the story says
 * so outright.
 *
 * Story 4.2.
 */
import { useCallback, useEffect, useState } from 'react'
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, ApiFailure } from '../../lib/api'
import { useMe } from '../../shell/useMe'
import { Menu, MenuButton } from '../../shell/Menu'
import { Avatar, AvatarStack } from '../../ui/Avatar'
import { Button } from '../../ui/Button'
import { Checkbox } from '../../ui/Checkbox'
import { Field } from '../../ui/Field'
import { Modal } from '../../ui/Modal'
import { Select } from '../../ui/Select'
import { Skeleton } from '../../ui/Skeleton'

type WorkspaceRole = 'editor' | 'reviewer' | 'viewer' | 'client'

type WorkspaceCard = {
  id: string
  name: string
  slug: string
  archivedAt: number | null
  funnelCount: number
  leadCount: number
  peopleCount: number
  people: { userId: string; name: string }[]
}

type WorkspacesView = {
  workspaces: WorkspaceCard[]
  limit: number
  used: number
  atLimit: boolean
}

type AccessEntry = {
  userId: string
  name: string
  email: string
  role: WorkspaceRole | null
  orgRole: 'owner' | 'admin' | null
  canExport: boolean
  canPublish: boolean
  piiUnredacted: boolean
}

type AccessView = {
  workspaceId: string
  workspaceName: string
  entries: AccessEntry[]
  invited: { id: string; email: string; role: string }[]
  roles: { role: WorkspaceRole; label: string; definition: string }[]
}

const UNLIMITED = -1

export function Workspaces() {
  const me = useMe()
  const orgId = me.status === 'ready' ? (me.me.orgs[0]?.id ?? null) : null

  const [view, setView] = useState<WorkspacesView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [renaming, setRenaming] = useState<WorkspaceCard | null>(null)
  const [archiving, setArchiving] = useState<WorkspaceCard | null>(null)
  const [access, setAccess] = useState<AccessView | null>(null)

  const load = useCallback(async () => {
    if (!orgId) return
    setError(null)
    try {
      setView(await apiGet<WorkspacesView>(`/v1/orgs/${orgId}/workspaces`))
    } catch (failure) {
      setView(null)
      setError(failure instanceof ApiFailure ? failure.message : 'Something went wrong.')
    }
  }, [orgId])

  useEffect(() => {
    void load()
  }, [load])

  const act = async (key: string, run: () => Promise<unknown>) => {
    setBusy(key)
    setError(null)
    try {
      await run()
      await load()
    } catch (failure) {
      setError(failure instanceof ApiFailure ? failure.message : 'Something went wrong.')
    } finally {
      setBusy(null)
    }
  }

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-x-mdx-24 gap-y-mdx-16">
      <div className="flex min-w-0 flex-1 basis-mdx-header-column flex-col gap-mdx-8">
        <h1 className="m-0 text-mdx-h1 font-mdx-bold leading-mdx-tight tracking-mdx-h1">
          Workspaces
        </h1>
        <p className="m-0 max-w-mdx-measure-60 text-mdx-16 text-mdx-slate">
          Workspaces keep clients separate. People you invite to one can't see the others.
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-x-mdx-16 gap-y-mdx-12">
        {view ? (
          <span className="text-mdx-15 tabular-nums text-mdx-slate">
            {view.limit === UNLIMITED
              ? `${view.used} in use`
              : `${view.used} of ${view.limit} used`}
          </span>
        ) : null}
        {view?.atLimit ? (
          <a
            href="/billing"
            className="text-mdx-15 font-mdx-semibold text-mdx-brand-deep no-underline"
          >
            Upgrade
          </a>
        ) : null}
        <Button
          onClick={() => setCreating(true)}
          disabled={!view || view.atLimit}
          title={
            view?.atLimit
              ? 'Every workspace on your plan is in use. Archive one you no longer need, or upgrade the plan.'
              : undefined
          }
        >
          New workspace
        </Button>
      </div>
    </div>
  )

  /* ── loading — three skeleton cards ───────────────────────────────────── */
  if (!view && !error) {
    return (
      <>
        {header}
        <Grid>
          {Array.from({ length: 3 }, (_, index) => (
            <Card key={index}>
              <Skeleton className="h-mdx-22 w-mdx-measure-34 max-w-full" />
              <Skeleton className="h-mdx-avatar-sm w-mdx-measure-34 max-w-full rounded-mdx-pill" />
              <Skeleton className="h-mdx-icon-button w-mdx-row-identity max-w-full" />
            </Card>
          ))}
        </Grid>
      </>
    )
  }

  /* ── error ────────────────────────────────────────────────────────────── */
  if (!view) {
    return (
      <>
        {header}
        <section className="flex flex-col items-center gap-mdx-14 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-28 py-mdx-48 text-center shadow-mdx-card">
          <span
            aria-hidden="true"
            className="flex size-mdx-40 shrink-0 items-center justify-center rounded-mdx-control bg-mdx-error text-mdx-20 font-mdx-bold text-mdx-white"
          >
            !
          </span>
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
            Couldn't load your workspaces
          </span>
          <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
            Every workspace and its leads are untouched, and nobody's access has changed. This page
            just couldn't read the list.
          </span>
          <Button onClick={() => void load()}>Try again</Button>
        </section>
      </>
    )
  }

  const openAccess = (workspace: WorkspaceCard) =>
    void act(workspace.id, async () => {
      setAccess(await apiGet<AccessView>(`/v1/orgs/${orgId}/workspaces/${workspace.id}/access`))
    })

  return (
    <>
      {header}

      {error ? (
        <p role="alert" className="m-0 text-mdx-15 text-mdx-error">
          {error}
        </p>
      ) : null}

      <Grid>
        {view.workspaces.map((workspace) => (
          <Card key={workspace.id}>
            <div className="flex items-start gap-mdx-12">
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-mdx-17 font-mdx-semibold tracking-mdx-h3">
                  {workspace.name}
                </span>
                <span className="text-mdx-15 tabular-nums text-mdx-slate">
                  {workspace.archivedAt === null
                    ? `${plural(workspace.funnelCount, 'funnel')} · ${plural(workspace.leadCount, 'lead')}`
                    : `Archived · ${plural(workspace.leadCount, 'lead')} kept`}
                </span>
              </span>
              <Menu
                label={`More actions for ${workspace.name}`}
                triggerClassName="flex size-mdx-icon-button shrink-0 cursor-pointer items-center justify-center rounded-mdx-control-sm border border-mdx-line bg-mdx-white text-mdx-15 text-mdx-slate transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground"
                trigger={<span aria-hidden="true">⋯</span>}
              >
                {workspace.archivedAt === null ? (
                  <>
                    <MenuButton onClick={() => setRenaming(workspace)}>Rename</MenuButton>
                    <MenuButton onClick={() => openAccess(workspace)}>Manage access</MenuButton>
                    <MenuButton destructive onClick={() => setArchiving(workspace)}>
                      Archive
                    </MenuButton>
                  </>
                ) : (
                  <MenuButton
                    onClick={() =>
                      void act(workspace.id, () =>
                        apiPatch(`/v1/orgs/${orgId}/workspaces/${workspace.id}`, { archived: false }),
                      )
                    }
                  >
                    Restore
                  </MenuButton>
                )}
              </Menu>
            </div>

            <AvatarStack
              people={workspace.people}
              total={workspace.peopleCount}
              label={plural(workspace.peopleCount, 'person', 'people')}
            />

            <Button
              variant="ghost"
              size="small"
              className="mt-auto self-start"
              disabled={busy === workspace.id || workspace.archivedAt !== null}
              onClick={() => openAccess(workspace)}
            >
              Manage access
            </Button>
          </Card>
        ))}
      </Grid>

      {creating ? (
        <NameModal
          title="New workspace"
          label="Workspace name"
          helper="Usually the client it is for. You can rename it later."
          confirm="Create workspace"
          busy={busy === 'create'}
          error={error}
          onClose={() => setCreating(false)}
          onSubmit={(name) =>
            act('create', async () => {
              await apiPost(`/v1/orgs/${orgId}/workspaces`, { name })
              setCreating(false)
            })
          }
        />
      ) : null}

      {renaming ? (
        <NameModal
          title={`Rename ${renaming.name}`}
          label="Workspace name"
          initial={renaming.name}
          confirm="Save name"
          busy={busy === renaming.id}
          error={error}
          onClose={() => setRenaming(null)}
          onSubmit={(name) =>
            act(renaming.id, async () => {
              await apiPatch(`/v1/orgs/${orgId}/workspaces/${renaming.id}`, { name })
              setRenaming(null)
            })
          }
        />
      ) : null}

      {archiving ? (
        <ArchiveModal
          workspace={archiving}
          busy={busy === archiving.id}
          error={error}
          onClose={() => setArchiving(null)}
          onConfirm={() =>
            act(archiving.id, async () => {
              await apiDelete(`/v1/orgs/${orgId}/workspaces/${archiving.id}`)
              setArchiving(null)
            })
          }
        />
      ) : null}

      {access ? (
        <AccessModal
          access={access}
          busy={busy === 'access'}
          error={error}
          onClose={() => setAccess(null)}
          onSave={(grants) =>
            act('access', async () => {
              await apiPut(`/v1/orgs/${orgId}/workspaces/${access.workspaceId}/access`, { grants })
              setAccess(null)
            })
          }
        />
      ) : null}
    </>
  )
}

/* ── the pieces ─────────────────────────────────────────────────────────── */

/** Three across at 1440, one at 375 — the mockup's `repeat(3, minmax(0,1fr))`. */
function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-mdx-16 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      {children}
    </section>
  )
}

/** "2 funnels · 412 leads" · "1 funnel" · "5 people" · "1 person". */
function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`
}

/** Create and rename are the same modal with different words. */
function NameModal({
  title,
  label,
  helper,
  initial = '',
  confirm,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  title: string
  label: string
  helper?: string
  initial?: string
  confirm: string
  busy: boolean
  error: string | null
  onClose: () => void
  onSubmit: (name: string) => void
}) {
  const [name, setName] = useState(initial)
  return (
    <Modal
      title={title}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy ? 'Saving' : undefined} onClick={() => onSubmit(name)}>
            {confirm}
          </Button>
        </>
      }
    >
      <Field
        label={label}
        value={name}
        maxLength={80}
        helper={helper}
        error={error ?? undefined}
        onChange={(event) => setName(event.target.value)}
      />
    </Modal>
  )
}

/**
 * The archive confirmation.
 *
 * The mockup's note is the whole design brief for it: "a destructive button,
 * but the copy leads with what is kept: the 96 leads stay readable and
 * exportable, and restoring puts it back." Every sentence below is the export's,
 * with the lead count interpolated where it interpolates one.
 */
function ArchiveModal({
  workspace,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  workspace: WorkspaceCard
  busy: boolean
  error: string | null
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <Modal
      title={`Archive ${workspace.name}?`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Keep it active
          </Button>
          <Button loading={busy ? 'Archiving' : undefined} onClick={onConfirm}>
            Archive workspace
          </Button>
        </>
      }
    >
      <p className="m-0 max-w-mdx-measure-52 text-mdx-15 leading-mdx-body text-mdx-slate">
        {workspace.funnelCount === 1 ? 'Its funnel stops' : 'Its funnels stop'} accepting new
        submissions and the workspace leaves the switcher.{' '}
        <span className="font-mdx-semibold text-mdx-ink">
          The {plural(workspace.leadCount, 'lead')} {workspace.leadCount === 1 ? 'is' : 'are'} kept
        </span>{' '}
        — you can read and export them, and restoring the workspace puts everything back.
      </p>
      <p className="m-0 max-w-mdx-measure-52 text-mdx-13 text-mdx-slate">
        Nothing is deleted. Only the owner can archive, and it appears in the audit log.
      </p>
      {error ? (
        <p role="alert" className="m-0 text-mdx-13 text-mdx-error">
          {error}
        </p>
      ) : null}
    </Modal>
  )
}

type DraftGrant = {
  userId?: string
  email?: string
  role: WorkspaceRole
  canExport: boolean
  canPublish: boolean
  piiUnredacted: boolean
  remove?: boolean
}

/**
 * Manage access.
 *
 * "Riverside Community Trust · roles here apply to this workspace only" — the
 * subtitle says the tenant rule outright, which is the point of the panel.
 *
 * THE THREE TOGGLES ARE NOT DRAWN ANYWHERE (Q-022 → DECISION-045). The story's
 * acceptance criterion asks for them by name — "role **plus** `can_export`,
 * `can_publish` and `pii_unredacted` as independent toggles" — and RULE-005 is
 * why they exist at all, so they are built with the Checkbox the design system
 * already specifies rather than left out because no frame shows them.
 * DECISION-045 kept the two workflow grants as checkboxes and separated the
 * third, which reveals personal data rather than permitting an action.
 */
function AccessModal({
  access,
  busy,
  error,
  onClose,
  onSave,
}: {
  access: AccessView
  busy: boolean
  error: string | null
  onClose: () => void
  onSave: (grants: DraftGrant[]) => void
}) {
  const grantable = access.entries.filter((entry) => entry.orgRole === null)
  const staff = access.entries.filter((entry) => entry.orgRole !== null)

  const [draft, setDraft] = useState<Record<string, DraftGrant>>(() =>
    Object.fromEntries(
      grantable.map((entry) => [
        entry.userId,
        {
          userId: entry.userId,
          role: entry.role ?? 'viewer',
          canExport: entry.canExport,
          canPublish: entry.canPublish,
          piiUnredacted: entry.piiUnredacted,
        },
      ]),
    ),
  )
  const [added, setAdded] = useState<DraftGrant[]>([])
  const [email, setEmail] = useState('')

  const update = (userId: string, patch: Partial<DraftGrant>) =>
    setDraft((current) => ({ ...current, [userId]: { ...current[userId]!, ...patch } }))

  return (
    <Modal
      title="Manage access"
      subtitle={`${access.workspaceName} · roles here apply to this workspace only.`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy ? 'Saving' : undefined}
            onClick={() => onSave([...Object.values(draft), ...added])}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-mdx-8">
        {staff.map((entry, index) => (
          <AccessRow key={entry.userId} name={entry.name} index={index}>
            <Identity
              name={entry.name}
              detail={`${entry.orgRole === 'owner' ? 'Owner' : 'Admin'} · every workspace`}
            />
          </AccessRow>
        ))}

        {grantable.map((entry, index) => (
          <AccessRow key={entry.userId} name={entry.name} index={staff.length + index}>
            <Identity name={entry.name} detail={entry.email} />
            <Select
              label={`${entry.name}'s role in this workspace`}
              className="flex-1 basis-mdx-row-control-sm"
              value={draft[entry.userId]?.role ?? 'viewer'}
              onChange={(event) => update(entry.userId, { role: event.target.value as WorkspaceRole })}
            >
              {access.roles.map((role) => (
                <option key={role.role} value={role.role}>
                  {role.label} — {role.definition}
                </option>
              ))}
            </Select>
            {/**
             * RULE-005 — separable from the role, never implied by it. Not
             * drawn on any frame (Q-022); built because the story asks for them
             * by name and because they are the whole reason the columns exist.
             */}
            <div className="flex w-full flex-col gap-mdx-6">
              <Checkbox
                label="Can export leads"
                checked={draft[entry.userId]?.canExport ?? false}
                onChange={(event) => update(entry.userId, { canExport: event.target.checked })}
              />
              <Checkbox
                label="Can publish funnels"
                checked={draft[entry.userId]?.canPublish ?? false}
                onChange={(event) => update(entry.userId, { canPublish: event.target.checked })}
              />
              {/**
               * `pii_unredacted` IS NOT A THIRD WORKFLOW TOGGLE (DECISION-045).
               *
               * The two above decide what somebody may DO. This one decides
               * what they may SEE, and DECISION-013 makes redaction one egress
               * function applied at every exit rather than a display
               * preference — so granting it changes the lead drawer, the
               * exports, the notifications and the dispatch payload at once.
               * A third identical checkbox reads as the same weight as
               * "Can publish funnels", which is exactly what it is not.
               *
               * SEPARATION AND A SENTENCE, NOT A COLOUR. `design/tokens.css`
               * has `--mdx-error` and no warning tint, and inventing one is a
               * design question rather than a component's call (CLAUDE.md).
               * The rule above it and the consequence written out below carry
               * the weight until a frame draws this panel.
               */}
              <div className="mt-mdx-4 flex flex-col gap-mdx-6 border-t border-mdx-line pt-mdx-8">
                <Checkbox
                  label="Can see personal details unredacted"
                  checked={draft[entry.userId]?.piiUnredacted ?? false}
                  onChange={(event) => update(entry.userId, { piiUnredacted: event.target.checked })}
                />
                <span className="max-w-mdx-measure-52 text-mdx-13 text-mdx-slate">
                  Names, emails and phone numbers appear in full — on screen, in exports and in
                  every notification. Left off, they stay masked everywhere.
                </span>
              </div>
            </div>
          </AccessRow>
        ))}

        {access.invited.map((invitation, index) => (
          <AccessRow key={invitation.id} name={invitation.email} index={index} muted>
            <Identity name={invitation.email} detail="Invited · not accepted yet" />
          </AccessRow>
        ))}

        {added.map((grant, index) => (
          <AccessRow key={grant.email} name={grant.email!} index={index} muted>
            <Identity
              name={grant.email!}
              detail={`Will be invited as ${grant.role} when you save`}
            />
          </AccessRow>
        ))}
      </div>

      {/* "The Add someone row takes an email rather than requiring the person
          to exist first" — the mockup's own note. */}
      <div className="flex flex-wrap items-end gap-mdx-12">
        <Field
          label="Add someone"
          type="email"
          value={email}
          className="min-w-0"
          helper="An address that isn't on the team yet gets an invitation to this workspace."
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button
          variant="secondary"
          disabled={email.trim().length === 0}
          onClick={() => {
            setAdded((current) => [
              ...current,
              {
                email: email.trim(),
                role: 'viewer',
                canExport: false,
                canPublish: false,
                piiUnredacted: false,
              },
            ])
            setEmail('')
          }}
        >
          Add
        </Button>
      </div>

      {error ? (
        <p role="alert" className="m-0 text-mdx-13 text-mdx-error">
          {error}
        </p>
      ) : null}
    </Modal>
  )
}

/**
 * One person inside the access panel — a tinted row, as the mockup draws it:
 * `--mdx-ground`, radius 10, `12px 14px`, the avatar then the identity then
 * whatever the caller puts beside them.
 */
function AccessRow({
  name,
  index,
  muted = false,
  children,
}: {
  name: string
  index: number
  muted?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-10 rounded-mdx-control bg-mdx-ground px-mdx-14 py-mdx-12">
      <Avatar name={name} index={index} muted={muted} />
      {children}
    </div>
  )
}

function Identity({ name, detail }: { name: string; detail: string }) {
  return (
    <span className="flex min-w-0 flex-1 basis-mdx-row-identity-sm flex-col">
      <span className="truncate text-mdx-15 font-mdx-semibold">{name}</span>
      <span className="truncate text-mdx-13 text-mdx-slate">{detail}</span>
    </span>
  )
}
